/**
 * Input devices for play mode, mirroring PlayerDevice: button presses / releases are latched as
 * "this frame" flags and handed to exactly one fixed step, sticks and held keys are read at the
 * step. Bindings follow Assets/PlayerInputActions.inputactions (Keyboard / Gamepad schemes);
 * the arrow-key scheme is the second keyboard player.
 */
import { emptyInput, type PlayerInput } from '../sim/game.ts';
import type { V2 } from '../sim/unity.ts';

export type DeviceKind = 'keyboard' | 'gamepad' | 'touch';

export abstract class InputDevice {
  abstract readonly id: string;
  abstract readonly label: string;
  abstract readonly kind: DeviceKind;
  protected edges = { jumpPressed: false, jumpReleased: false, dashPressed: false, interactPressed: false, powerupPressed: false, powerupReleased: false, restartPressed: false, claimCameraPressed: false, dUp: false, dDown: false, dLeft: false, dRight: false, pausePressed: false };
  protected activity = false;
  connected = true;

  /** a button was pressed since the last call (join / claim a player) */
  takeActivity(): boolean {
    const a = this.activity;
    this.activity = false;
    return a;
  }

  protected abstract movement(): V2;
  protected control(): V2 {
    return { x: 0, y: 0 };
  }
  protected abstract interactDown(): boolean;
  controlEnabled(): boolean {
    return false;
  }
  leftTrigger(): number {
    return 0;
  }
  rightTrigger(): number {
    return 0;
  }

  /** Interact + d-pad up: this device moves to another player. Consumes the d-pad press. */
  takeCycle(): boolean {
    if (!this.interactDown() || !this.edges.dUp) return false;
    this.edges.dUp = false;
    return true;
  }

  /** Left trigger + d-pad up: join another player and move this device to it. */
  takeSpawn(): boolean {
    if (this.leftTrigger() <= 0 || !this.edges.dUp) return false;
    this.edges.dUp = false;
    return true;
  }

  /** PlayerDevice.GetInputData for one fixed step */
  consume(): PlayerInput {
    const i = emptyInput();
    if (this.connected) {
      i.movement = this.movement();
      i.control = this.control();
      i.interactDown = this.interactDown();
      i.controlEnabled = this.controlEnabled();
      i.leftTrigger = this.leftTrigger();
      i.rightTrigger = this.rightTrigger();
      Object.assign(i, this.edges);
    }
    this.clearEdges();
    return i;
  }

  clearEdges() {
    for (const k of Object.keys(this.edges) as (keyof typeof this.edges)[]) this.edges[k] = false;
  }

  protected press(action: Action) {
    this.activity = true;
    switch (action) {
      case 'jump': this.edges.jumpPressed = true; break;
      case 'dash': this.edges.dashPressed = true; break;
      case 'interact': this.edges.interactPressed = true; break;
      case 'powerup': this.edges.powerupPressed = true; break;
      case 'restart': this.edges.restartPressed = true; break;
      case 'claim': this.edges.claimCameraPressed = true; break;
      case 'dUp': this.edges.dUp = true; break;
      case 'dDown': this.edges.dDown = true; break;
      case 'dLeft': this.edges.dLeft = true; break;
      case 'dRight': this.edges.dRight = true; break;
      case 'pause': this.edges.pausePressed = true; break;
    }
  }

  protected release(action: Action) {
    if (action === 'jump') this.edges.jumpReleased = true;
    else if (action === 'powerup') this.edges.powerupReleased = true;
  }
}

type Action = 'jump' | 'dash' | 'interact' | 'powerup' | 'restart' | 'claim' | 'dUp' | 'dDown' | 'dLeft' | 'dRight' | 'pause';

// ---------- keyboard ----------

export interface KeyScheme {
  id: string;
  label: string;
  up: string[];
  down: string[];
  left: string[];
  right: string[];
  /** the "Control" vector (camera pan / debug flight); the game binds it to the arrows, which belong to P2 here */
  control?: { up: string[]; down: string[]; left: string[]; right: string[] };
  /** Ctrl / held. With the control vector, the player flies. */
  controlEnable?: string[];
  triggers?: { left: string[]; right: string[] };
  actions: Record<Action, string[]>;
}

export const KEY_SCHEMES: KeyScheme[] = [
  {
    id: 'kb-wasd',
    label: 'Keyboard (WASD)',
    up: ['KeyW'],
    down: ['KeyS'],
    left: ['KeyA'],
    right: ['KeyD'],
    control: { up: ['KeyI'], down: ['KeyK'], left: ['KeyJ'], right: ['KeyL'] },
    controlEnable: ['ControlLeft', 'ControlRight'],
    triggers: { left: ['KeyZ'], right: ['KeyX'] },
    actions: {
      jump: ['Space', 'KeyW'],
      dash: ['ShiftLeft', 'KeyF'],
      interact: ['Enter', 'KeyE'],
      powerup: ['KeyQ'],
      restart: ['Backspace', 'KeyR'],
      claim: ['Tab'],
      dUp: ['BracketRight'],
      dDown: ['Tab'],
      dLeft: ['Minus'],
      dRight: ['Equal'],
      pause: ['KeyP'],
    },
  },
  {
    id: 'kb-arrows',
    label: 'Keyboard (arrows)',
    up: ['ArrowUp'],
    down: ['ArrowDown'],
    left: ['ArrowLeft'],
    right: ['ArrowRight'],
    actions: {
      jump: ['ArrowUp', 'Slash', 'Numpad0'],
      dash: ['ShiftRight', 'Period', 'NumpadDecimal'],
      interact: ['Comma', 'NumpadEnter'],
      powerup: ['Quote', 'NumpadAdd'],
      restart: ['Delete'],
      claim: ['Semicolon'],
      dUp: [],
      dDown: [],
      dLeft: [],
      dRight: [],
      pause: ['NumpadMultiply'],
    },
  },
];

/** 2D Vector composite in DigitalNormalized mode */
function digital(l: boolean, r: boolean, d: boolean, u: boolean): V2 {
  const x = (r ? 1 : 0) - (l ? 1 : 0), y = (u ? 1 : 0) - (d ? 1 : 0);
  if (x && y) return { x: x * Math.SQRT1_2, y: y * Math.SQRT1_2 };
  return { x, y };
}

export class KeyboardDevice extends InputDevice {
  readonly kind = 'keyboard';
  readonly id: string;
  readonly label: string;
  private held = new Set<string>();
  private actionHeld = new Map<Action, number>();

  constructor(readonly scheme: KeyScheme) {
    super();
    this.id = scheme.id;
    this.label = scheme.label;
  }

  owns(code: string): boolean {
    const s = this.scheme;
    const c = s.control;
    const t = s.triggers;
    return [s.up, s.down, s.left, s.right, ...(c ? [c.up, c.down, c.left, c.right] : []), ...(s.controlEnable ?? []), ...(t ? [...t.left, ...t.right] : []), ...Object.values(s.actions)].some((l) => l.includes(code));
  }

  override controlEnabled(): boolean {
    return (this.scheme.controlEnable ?? []).some((c) => this.held.has(c));
  }

  override leftTrigger(): number {
    return (this.scheme.triggers?.left ?? []).some((c) => this.held.has(c)) ? 1 : 0;
  }

  override rightTrigger(): number {
    return (this.scheme.triggers?.right ?? []).some((c) => this.held.has(c)) ? 1 : 0;
  }

  keyDown(code: string) {
    if (this.held.has(code)) return;
    this.held.add(code);
    this.activity = true;
    for (const [a, codes] of Object.entries(this.scheme.actions) as [Action, string[]][]) {
      if (!codes.includes(code)) continue;
      const n = this.actionHeld.get(a) ?? 0;
      this.actionHeld.set(a, n + 1);
      if (n === 0) this.press(a);
    }
  }

  keyUp(code: string) {
    if (!this.held.delete(code)) return;
    for (const [a, codes] of Object.entries(this.scheme.actions) as [Action, string[]][]) {
      if (!codes.includes(code)) continue;
      const n = Math.max(0, (this.actionHeld.get(a) ?? 0) - 1);
      this.actionHeld.set(a, n);
      if (n === 0) this.release(a);
    }
  }

  /** mouse buttons act on the first keyboard player: left = dash, right = interact */
  mouseDown(button: number) {
    if (button === 0) this.press('dash');
    else if (button === 2) this.press('interact');
  }

  releaseAll() {
    for (const c of [...this.held]) this.keyUp(c);
  }

  protected movement(): V2 {
    const h = (l: string[]) => l.some((c) => this.held.has(c));
    const s = this.scheme;
    return digital(h(s.left), h(s.right), h(s.down), h(s.up));
  }

  protected override control(): V2 {
    const c = this.scheme.control;
    if (!c) return { x: 0, y: 0 };
    const h = (l: string[]) => l.some((k) => this.held.has(k));
    return digital(h(c.left), h(c.right), h(c.down), h(c.up));
  }

  protected interactDown(): boolean {
    return (this.actionHeld.get('interact') ?? 0) > 0;
  }
}

// ---------- gamepad ----------

/** standard-mapping button indices. Y/north is a dash face button, never the fly modifier (that's LB). */
const GP = { south: 0, east: 1, west: 2, north: 3, lb: 4, rb: 5, lt: 6, rt: 7, select: 8, start: 9, dUp: 12, dDown: 13, dLeft: 14, dRight: 15 };

const GAMEPAD_ACTIONS: [number, Action][] = [
  [GP.south, 'jump'],
  [GP.west, 'dash'],
  [GP.north, 'dash'],
  [GP.east, 'interact'],
  [GP.rb, 'powerup'],
  [GP.select, 'restart'],
  [GP.start, 'pause'],
  [GP.dDown, 'claim'],
  [GP.dUp, 'dUp'],
  [GP.dDown, 'dDown'],
  [GP.dLeft, 'dLeft'],
  [GP.dRight, 'dRight'],
];

function btnDown(b: GamepadButton | number | undefined): boolean {
  if (b == null) return false;
  if (typeof b === 'number') return b >= 0.5;
  return b.pressed || b.value >= 0.5;
}

function btnValue(b: GamepadButton | number | undefined): number {
  if (b == null) return 0;
  if (typeof b === 'number') return b;
  return b.value;
}

/** StickDeadzone processor defaults (min 0.125, max 0.925), y up */
function stick(x: number, y: number): V2 {
  const m = Math.hypot(x, y);
  if (m < 0.125) return { x: 0, y: 0 };
  const s = Math.min(1, (m - 0.125) / (0.925 - 0.125)) / m;
  return { x: x * s, y: -y * s };
}

/** True when an axis looks like an analog trigger parked at rest (−1 in DirectInput). */
function triggerRest(v: number): boolean {
  return v < -0.875;
}

/**
 * Right stick. Standard mapping is axes 2/3. Unmapped DualShock sits RY on axis 5 with
 * L2/R2 on 3/4; unmapped Xbox HID sits RX/RY on 3/4 with triggers on 2/5. Using a trigger
 * at rest (−1) as the stick makes every shoulder press fly.
 */
function rightStick(pad: Gamepad): V2 {
  const a = pad.axes;
  const id = pad.id.toLowerCase();
  if (pad.mapping === 'standard') return stick(a[2] ?? 0, a[3] ?? 0);
  const sony = /dualshock|dualsense|playstation|054c|wireless controller/.test(id);
  if (sony && a.length >= 6) return stick(a[2] ?? 0, a[5] ?? 0);
  if (a.length >= 5 && (triggerRest(a[2] ?? 0) || /xbox|xinput|045e|microsoft/.test(id))) return stick(a[3] ?? 0, a[4] ?? 0);
  if (a.length >= 6 && triggerRest(a[3] ?? 0) && triggerRest(a[4] ?? 0)) return stick(a[2] ?? 0, a[5] ?? 0);
  return stick(a[2] ?? 0, a[3] ?? 0);
}

export class GamepadDevice extends InputDevice {
  readonly kind = 'gamepad';
  readonly id: string;
  readonly label: string;
  private prev: boolean[] = [];
  private pad: Gamepad | null = null;

  constructor(readonly index: number, name: string) {
    super();
    this.id = `gp:${index}`;
    this.label = `Gamepad ${index + 1}${name ? ` (${name.replace(/\s*\(.*$/, '').slice(0, 24)})` : ''}`;
  }

  update(pad: Gamepad | null) {
    this.pad = pad;
    this.connected = !!pad?.connected;
    if (!pad) return;
    const now = pad.buttons.map((b) => btnDown(b));
    for (let i = 0; i < now.length; i++) if (now[i] && !this.prev[i]) this.activity = true;
    for (const [b, a] of GAMEPAD_ACTIONS) {
      if (now[b] && !this.prev[b]) this.press(a);
      if (!now[b] && this.prev[b]) this.release(a);
    }
    this.prev = now;
  }

  protected movement(): V2 {
    const p = this.pad;
    return p ? stick(p.axes[0] ?? 0, p.axes[1] ?? 0) : { x: 0, y: 0 };
  }

  protected control(): V2 {
    return this.pad ? rightStick(this.pad) : { x: 0, y: 0 };
  }

  /** Unity ControlEnable is left shoulder. X/Y are dash and must never start fly. */
  override controlEnabled(): boolean {
    const p = this.pad;
    if (!p || btnDown(p.buttons[GP.north]) || btnDown(p.buttons[GP.west])) return false;
    return btnDown(p.buttons[GP.lb]);
  }

  override leftTrigger(): number {
    const p = this.pad;
    if (!p) return 0;
    const b = btnValue(p.buttons[GP.lt]);
    if (b > 0) return b;
    if (p.mapping !== 'standard' && p.axes.length >= 6) {
      const v = p.axes[2] ?? 0;
      if (triggerRest(v) || v > 0) return triggerRest(v) ? 0 : v;
    }
    return b;
  }

  override rightTrigger(): number {
    const p = this.pad;
    if (!p) return 0;
    const b = btnValue(p.buttons[GP.rt]);
    if (b > 0) return b;
    if (p.mapping !== 'standard' && p.axes.length >= 6) {
      const v = p.axes[5] ?? p.axes[4] ?? 0;
      if (triggerRest(v) || v > 0) return triggerRest(v) ? 0 : v;
    }
    return b;
  }

  protected interactDown(): boolean {
    return btnDown(this.pad?.buttons[GP.east]);
  }
}

// ---------- touch ----------

/** virtual stick + buttons, driven by TouchControls */
export class TouchDevice extends InputDevice {
  readonly kind = 'touch';
  readonly id = 'touch';
  readonly label = 'Touch';
  stick: V2 = { x: 0, y: 0 };
  private down = new Set<Action>();

  button(action: Action, pressed: boolean) {
    if (pressed === this.down.has(action)) return;
    if (pressed) {
      this.down.add(action);
      this.press(action);
    } else {
      this.down.delete(action);
      this.release(action);
    }
  }

  touched() {
    this.activity = true;
  }

  isDown(action: Action) {
    return this.down.has(action);
  }

  protected movement(): V2 {
    return { ...this.stick };
  }

  protected interactDown(): boolean {
    return this.down.has('interact');
  }
}

export type { Action };
